import type { Actuator, Body, BodyUUID, Geom, Joint, Scene, Tendon } from "../types/scene";

export type ModelValidationSeverity = "error" | "warning" | "info";

export type ModelValidationTargetKind =
  | "actuator"
  | "body"
  | "camera"
  | "contact"
  | "equality"
  | "geom"
  | "hfield"
  | "joint"
  | "mesh"
  | "sensor"
  | "site"
  | "tendon";

export interface ModelValidationTarget {
  kind: ModelValidationTargetKind;
  uuid: string;
  label?: string;
}

export interface ModelValidationFinding {
  id: string;
  ruleId: string;
  severity: ModelValidationSeverity;
  category: "integrity" | "physics";
  title: string;
  message: string;
  targets: ModelValidationTarget[];
}

export interface ModelValidationSummary {
  errorCount: number;
  warningCount: number;
  infoCount: number;
  findingCount: number;
}

export interface ModelValidationResult {
  findings: ModelValidationFinding[];
  summary: ModelValidationSummary;
  checkedAt: number;
}

type RuleContext = {
  scene: Scene;
  geomBodyByUuid: Map<string, Body>;
  geomsByUuid: Map<string, Geom>;
};

type FindingInput = Omit<ModelValidationFinding, "id">;

type ModelValidationRule = {
  id: string;
  run: (context: RuleContext) => FindingInput[];
};

const EPSILON = 1e-9;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const bodyLabel = (body?: Body) => body?.name || body?.uuid || "Unknown body";
const jointLabel = (joint?: Joint) => joint?.name || joint?.uuid || "Unknown joint";
const actuatorLabel = (actuator?: Actuator) =>
  actuator?.name || actuator?.uuid || "Unknown actuator";
const tendonLabel = (tendon?: Tendon) => tendon?.name || tendon?.uuid || "Unknown tendon";
const geomLabel = (geom?: Geom) => geom?.name || geom?.uuid || "Unnamed geom";

const sensorAttachTypeByKind: Record<string, ModelValidationTargetKind | null> = {
  gyro: "site",
  accelerometer: "site",
  velocimeter: "site",
  force: "site",
  torque: "site",
  touch: "site",
  magnetometer: "site",
  ballquat: "site",
  ballangvel: "site",
  jointpos: "joint",
  jointvel: "joint",
  jointactuatorfrc: "joint",
  jointlimitfrc: "joint",
  actuatorpos: "actuator",
  actuatorvel: "actuator",
  actuatorfrc: "actuator",
  subtreecom: "body",
  subtreelinvel: "body",
  subtreeangmom: "body",
  tendonpos: "tendon",
  tendonvel: "tendon",
  clock: null,
};

const attachAttrByKind: Partial<Record<ModelValidationTargetKind, string>> = {
  actuator: "actuator",
  body: "body",
  joint: "joint",
  site: "site",
  tendon: "tendon",
};

const target = (
  kind: ModelValidationTargetKind,
  uuid: string,
  label?: string
): ModelValidationTarget => ({ kind, uuid, label });

const buildSceneIndexes = (scene: Scene): Pick<RuleContext, "geomBodyByUuid" | "geomsByUuid"> => {
  const geomBodyByUuid = new Map<string, Body>();
  const geomsByUuid = new Map<string, Geom>();

  Object.values(scene.bodies).forEach((body) => {
    (body.geoms || []).forEach((geom) => {
      geomsByUuid.set(geom.uuid, geom);
      geomBodyByUuid.set(geom.uuid, body);
    });
  });

  return { geomBodyByUuid, geomsByUuid };
};

const hasRecordKey = <T>(record: Record<string, T>, value: string) =>
  Object.prototype.hasOwnProperty.call(record, value);

const recordHasUuidOrName = (
  record: Record<string, { uuid?: string; name?: string }>,
  value: string
) =>
  Boolean(
    hasRecordKey(record, value) ||
    Object.values(record).some((item) => item.uuid === value || item.name === value)
  );

const sceneRecordForKind = (scene: Scene, kind: ModelValidationTargetKind) => {
  if (kind === "actuator") return scene.actuators;
  if (kind === "body") return scene.bodies;
  if (kind === "camera") return scene.cameras;
  if (kind === "joint") return scene.joints;
  if (kind === "site") return scene.sites;
  if (kind === "tendon") return scene.tendons;
  return null;
};

const hasPositiveGeomMassHint = (geom: Geom) => {
  if (geom.visualOnly) return false;
  if (isFiniteNumber(geom.mass)) return geom.mass > EPSILON;
  if (isFiniteNumber(geom.density)) return geom.density > EPSILON;
  return true;
};

const bodyHasMassContributor = (body: Body) =>
  Boolean(body.inertia && body.inertia.mass > EPSILON) ||
  (body.geoms || []).some((geom) => hasPositiveGeomMassHint(geom));

const isMovingBody = (body: Body) => Boolean(body.joints?.length);

const validateDanglingReferences: ModelValidationRule = {
  id: "dangling-references",
  run: ({ scene, geomsByUuid }) => {
    const findings: FindingInput[] = [];

    const addDanglingReference = ({
      ownerKind,
      ownerUuid,
      ownerLabel,
      missingKind,
      missingUuid,
      message,
    }: {
      ownerKind: ModelValidationTargetKind;
      ownerUuid: string;
      ownerLabel?: string;
      missingKind: ModelValidationTargetKind;
      missingUuid: string;
      message: string;
    }) => {
      findings.push({
        ruleId: "dangling-references",
        severity: "error",
        category: "integrity",
        title: "Dangling reference",
        message,
        targets: [target(ownerKind, ownerUuid, ownerLabel), target(missingKind, missingUuid)],
      });
    };

    Object.values(scene.bodies).forEach((body) => {
      (body.children || []).forEach((childUuid) => {
        if (!scene.bodies[childUuid]) {
          addDanglingReference({
            ownerKind: "body",
            ownerUuid: body.uuid,
            ownerLabel: bodyLabel(body),
            missingKind: "body",
            missingUuid: childUuid,
            message: `${bodyLabel(body)} references missing child body ${childUuid}.`,
          });
        }
      });
      (body.joints || []).forEach((jointUuid) => {
        if (!scene.joints[jointUuid]) {
          addDanglingReference({
            ownerKind: "body",
            ownerUuid: body.uuid,
            ownerLabel: bodyLabel(body),
            missingKind: "joint",
            missingUuid: jointUuid,
            message: `${bodyLabel(body)} references missing joint ${jointUuid}.`,
          });
        }
      });
      (body.sites || []).forEach((siteUuid) => {
        if (!scene.sites[siteUuid]) {
          addDanglingReference({
            ownerKind: "body",
            ownerUuid: body.uuid,
            ownerLabel: bodyLabel(body),
            missingKind: "site",
            missingUuid: siteUuid,
            message: `${bodyLabel(body)} references missing site ${siteUuid}.`,
          });
        }
      });
      (body.geoms || []).forEach((geom) => {
        if (geom.mesh && !scene.meshes[geom.mesh]) {
          addDanglingReference({
            ownerKind: "geom",
            ownerUuid: geom.uuid,
            ownerLabel: geomLabel(geom),
            missingKind: "mesh",
            missingUuid: geom.mesh,
            message: `${geomLabel(geom)} references missing mesh ${geom.mesh}.`,
          });
        }
        if (geom.heightField && !scene.hfields[geom.heightField]) {
          addDanglingReference({
            ownerKind: "geom",
            ownerUuid: geom.uuid,
            ownerLabel: geomLabel(geom),
            missingKind: "hfield",
            missingUuid: geom.heightField,
            message: `${geomLabel(geom)} references missing height field ${geom.heightField}.`,
          });
        }
      });
    });

    scene.worldBodies.forEach((worldBody) => {
      if (!scene.bodies[worldBody.uuid]) {
        addDanglingReference({
          ownerKind: "body",
          ownerUuid: worldBody.uuid,
          ownerLabel: worldBody.name,
          missingKind: "body",
          missingUuid: worldBody.uuid,
          message: `World body ${worldBody.name || worldBody.uuid} is not present in bodies.`,
        });
      }
    });

    Object.values(scene.actuators).forEach((actuator) => {
      if (actuator.joint && !scene.joints[actuator.joint]) {
        addDanglingReference({
          ownerKind: "actuator",
          ownerUuid: actuator.uuid,
          ownerLabel: actuatorLabel(actuator),
          missingKind: "joint",
          missingUuid: actuator.joint,
          message: `${actuatorLabel(actuator)} targets missing joint ${actuator.joint}.`,
        });
      }
      if (actuator.tendonUuid && !scene.tendons[actuator.tendonUuid]) {
        addDanglingReference({
          ownerKind: "actuator",
          ownerUuid: actuator.uuid,
          ownerLabel: actuatorLabel(actuator),
          missingKind: "tendon",
          missingUuid: actuator.tendonUuid,
          message: `${actuatorLabel(actuator)} targets missing tendon ${actuator.tendonUuid}.`,
        });
      }
    });

    Object.values(scene.tendons).forEach((tendon) => {
      tendon.joints.forEach((jointRef) => {
        if (!scene.joints[jointRef.joint]) {
          addDanglingReference({
            ownerKind: "tendon",
            ownerUuid: tendon.uuid,
            ownerLabel: tendonLabel(tendon),
            missingKind: "joint",
            missingUuid: jointRef.joint,
            message: `${tendonLabel(tendon)} references missing joint ${jointRef.joint}.`,
          });
        }
      });
      (tendon.spatial || []).forEach((spatialRef) => {
        if (
          spatialRef.kind === "site" &&
          !recordHasUuidOrName(scene.sites, String(spatialRef.site))
        ) {
          addDanglingReference({
            ownerKind: "tendon",
            ownerUuid: tendon.uuid,
            ownerLabel: tendonLabel(tendon),
            missingKind: "site",
            missingUuid: String(spatialRef.site),
            message: `${tendonLabel(tendon)} references missing site ${spatialRef.site}.`,
          });
        }
        if (spatialRef.kind === "geom" && !geomsByUuid.has(spatialRef.geom)) {
          addDanglingReference({
            ownerKind: "tendon",
            ownerUuid: tendon.uuid,
            ownerLabel: tendonLabel(tendon),
            missingKind: "geom",
            missingUuid: spatialRef.geom,
            message: `${tendonLabel(tendon)} references missing wrapping geom ${spatialRef.geom}.`,
          });
        }
        if (
          spatialRef.kind === "geom" &&
          spatialRef.sidesite &&
          !recordHasUuidOrName(scene.sites, String(spatialRef.sidesite))
        ) {
          addDanglingReference({
            ownerKind: "tendon",
            ownerUuid: tendon.uuid,
            ownerLabel: tendonLabel(tendon),
            missingKind: "site",
            missingUuid: String(spatialRef.sidesite),
            message: `${tendonLabel(tendon)} references missing side site ${spatialRef.sidesite}.`,
          });
        }
      });
    });

    Object.values(scene.equality).forEach((equality) => {
      const expectedMap =
        equality.objtype === "body"
          ? scene.bodies
          : equality.objtype === "joint"
            ? scene.joints
            : equality.objtype === "tendon"
              ? scene.tendons
              : equality.objtype === "site"
                ? scene.sites
                : null;
      const equalityLabel = equality.name || equality.uuid;
      [equality.obj1, equality.obj2].forEach((objectUuid) => {
        if (!objectUuid) return;
        const objectUuidString = String(objectUuid);
        const exists =
          equality.objtype === "geom"
            ? geomsByUuid.has(objectUuidString)
            : expectedMap
              ? hasRecordKey(expectedMap as Record<string, unknown>, objectUuidString)
              : hasRecordKey(scene.bodies, objectUuidString) ||
                hasRecordKey(scene.joints, objectUuidString) ||
                hasRecordKey(scene.tendons, objectUuidString) ||
                hasRecordKey(scene.sites, objectUuidString) ||
                geomsByUuid.has(objectUuidString);
        if (!exists) {
          addDanglingReference({
            ownerKind: "equality",
            ownerUuid: equality.uuid,
            ownerLabel: equalityLabel,
            missingKind: equality.objtype || "body",
            missingUuid: objectUuid,
            message: `${equalityLabel} references missing ${equality.objtype || "object"} ${objectUuid}.`,
          });
        }
      });
    });

    scene.contactPairs.forEach((pair) => {
      [pair.geom1, pair.geom2].forEach((geomUuid) => {
        if (!geomsByUuid.has(geomUuid)) {
          addDanglingReference({
            ownerKind: "contact",
            ownerUuid: pair.uuid,
            ownerLabel: pair.name || pair.uuid,
            missingKind: "geom",
            missingUuid: geomUuid,
            message: `Contact pair ${pair.name || pair.uuid} references missing geom ${geomUuid}.`,
          });
        }
      });
    });

    scene.contactExcludes.forEach((exclude) => {
      [exclude.body1, exclude.body2].forEach((bodyUuid) => {
        if (!scene.bodies[bodyUuid]) {
          addDanglingReference({
            ownerKind: "contact",
            ownerUuid: `${exclude.body1}:${exclude.body2}`,
            missingKind: "body",
            missingUuid: bodyUuid,
            message: `Contact exclude references missing body ${bodyUuid}.`,
          });
        }
      });
    });

    Object.values(scene.cameras).forEach((camera) => {
      if (camera.mountBodyUuid && !scene.bodies[camera.mountBodyUuid]) {
        addDanglingReference({
          ownerKind: "camera",
          ownerUuid: camera.uuid,
          ownerLabel: camera.name,
          missingKind: "body",
          missingUuid: camera.mountBodyUuid,
          message: `${camera.name} is mounted to missing body ${camera.mountBodyUuid}.`,
        });
      }
      if (camera.mountSiteUuid && !scene.sites[camera.mountSiteUuid]) {
        addDanglingReference({
          ownerKind: "camera",
          ownerUuid: camera.uuid,
          ownerLabel: camera.name,
          missingKind: "site",
          missingUuid: camera.mountSiteUuid,
          message: `${camera.name} is mounted to missing site ${camera.mountSiteUuid}.`,
        });
      }
      if (camera.targetBodyUuid && !scene.bodies[camera.targetBodyUuid]) {
        addDanglingReference({
          ownerKind: "camera",
          ownerUuid: camera.uuid,
          ownerLabel: camera.name,
          missingKind: "body",
          missingUuid: camera.targetBodyUuid,
          message: `${camera.name} tracks missing body ${camera.targetBodyUuid}.`,
        });
      }
    });

    Object.values(scene.sensors).forEach((sensor) => {
      const attachKind = sensor.kind.startsWith("tendon")
        ? "tendon"
        : sensorAttachTypeByKind[sensor.kind];
      const attrKey = attachKind ? attachAttrByKind[attachKind] : null;
      const attrValue = attrKey ? sensor.attrs?.[attrKey] : "";

      if (attachKind && attrValue) {
        const record = sceneRecordForKind(scene, attachKind);
        if (record && !recordHasUuidOrName(record, attrValue)) {
          addDanglingReference({
            ownerKind: "sensor",
            ownerUuid: sensor.uuid,
            ownerLabel: sensor.name,
            missingKind: attachKind,
            missingUuid: attrValue,
            message: `${sensor.name} references missing ${attachKind} ${attrValue}.`,
          });
        }
      }

      if (sensor.kind.startsWith("frame")) {
        const objtype = String(sensor.attrs?.objtype || "").toLowerCase();
        const objname = String(sensor.attrs?.objname || "");
        const frameKind = objtype.includes("joint")
          ? "joint"
          : objtype.includes("site")
            ? "site"
            : objtype.includes("geom")
              ? "geom"
              : objtype.includes("camera")
                ? "camera"
                : objtype.includes("actuator")
                  ? "actuator"
                  : objtype.includes("body") || objtype.includes("xbody")
                    ? "body"
                    : null;

        if (frameKind && objname) {
          const exists =
            frameKind === "geom"
              ? geomsByUuid.has(objname) ||
                Array.from(geomsByUuid.values()).some((geom) => geom.name === objname)
              : recordHasUuidOrName(sceneRecordForKind(scene, frameKind) || {}, objname);
          if (!exists) {
            addDanglingReference({
              ownerKind: "sensor",
              ownerUuid: sensor.uuid,
              ownerLabel: sensor.name,
              missingKind: frameKind,
              missingUuid: objname,
              message: `${sensor.name} references missing ${frameKind} ${objname}.`,
            });
          }
        }
      }
    });

    return findings;
  },
};

const validateDegenerateInertials: ModelValidationRule = {
  id: "degenerate-inertials",
  run: ({ scene }) =>
    Object.values(scene.bodies).flatMap((body) => {
      const inertia = body.inertia;
      if (!inertia) return [];
      const findings: FindingInput[] = [];
      if (!isFiniteNumber(inertia.mass) || inertia.mass <= EPSILON) {
        findings.push({
          ruleId: "degenerate-inertials",
          severity: "error",
          category: "physics",
          title: "Invalid inertial mass",
          message: `${bodyLabel(body)} has inertial mass ${inertia.mass}. Mass must be positive.`,
          targets: [target("body", body.uuid, bodyLabel(body))],
        });
      }
      if (inertia.diagonal.some((value) => !isFiniteNumber(value) || value <= EPSILON)) {
        findings.push({
          ruleId: "degenerate-inertials",
          severity: "error",
          category: "physics",
          title: "Invalid diagonal inertia",
          message: `${bodyLabel(body)} has a non-positive diagonal inertia component.`,
          targets: [target("body", body.uuid, bodyLabel(body))],
        });
      }
      if (inertia.fullinertia) {
        const [ixx, iyy, izz] = inertia.fullinertia;
        const violatesTriangleInequality =
          ixx + iyy < izz - EPSILON || ixx + izz < iyy - EPSILON || iyy + izz < ixx - EPSILON;
        if (
          [ixx, iyy, izz].some((value) => !isFiniteNumber(value) || value <= EPSILON) ||
          violatesTriangleInequality
        ) {
          findings.push({
            ruleId: "degenerate-inertials",
            severity: "error",
            category: "physics",
            title: "Invalid full inertia",
            message: `${bodyLabel(body)} has full inertia values that are non-positive or violate the inertia triangle inequality.`,
            targets: [target("body", body.uuid, bodyLabel(body))],
          });
        }
      }
      return findings;
    }),
};

const validateMasslessMovingBodies: ModelValidationRule = {
  id: "massless-moving-bodies",
  run: ({ scene }) =>
    Object.values(scene.bodies)
      .filter((body) => isMovingBody(body) && !bodyHasMassContributor(body))
      .map((body) => ({
        ruleId: "massless-moving-bodies",
        severity: "warning",
        category: "physics",
        title: "Moving body may be massless",
        message: `${bodyLabel(body)} has joints but no inertial block or mass-contributing geoms.`,
        targets: [target("body", body.uuid, bodyLabel(body))],
      })),
};

const validateEmptyMovingBodies: ModelValidationRule = {
  id: "empty-moving-bodies",
  run: ({ scene }) =>
    Object.values(scene.bodies)
      .filter(
        (body) =>
          isMovingBody(body) &&
          !body.inertia &&
          (body.geoms || []).length === 0 &&
          body.children.length === 0
      )
      .map((body) => ({
        ruleId: "empty-moving-bodies",
        severity: "warning",
        category: "physics",
        title: "Moving body has no payload",
        message: `${bodyLabel(body)} has joints but no geoms, inertial, or child bodies.`,
        targets: [target("body", body.uuid, bodyLabel(body))],
      })),
};

const validateJointLimits: ModelValidationRule = {
  id: "joint-limits",
  run: ({ scene }) =>
    Object.values(scene.joints).flatMap((joint) => {
      if (joint.kind === "free") return [];
      if (joint.kind === "ball") {
        if (joint.useLimits === "false") {
          return [
            {
              ruleId: "joint-limits",
              severity: "info",
              category: "physics",
              title: "Ball joint has no limit",
              message: `${jointLabel(joint)} has limits disabled.`,
              targets: [target("joint", joint.uuid, jointLabel(joint))],
            },
          ];
        }
        if (!isFiniteNumber(joint.max_rotation) || joint.max_rotation <= EPSILON) {
          return [
            {
              ruleId: "joint-limits",
              severity: "warning",
              category: "physics",
              title: "Ball joint has degenerate limit",
              message: `${jointLabel(joint)} has max rotation ${joint.max_rotation}.`,
              targets: [target("joint", joint.uuid, jointLabel(joint))],
            },
          ];
        }
        return [];
      }

      const findings: FindingInput[] = [];
      if (joint.useLimits === "false") {
        findings.push({
          ruleId: "joint-limits",
          severity: "info",
          category: "physics",
          title: "Joint has no limit",
          message: `${jointLabel(joint)} has limits disabled.`,
          targets: [target("joint", joint.uuid, jointLabel(joint))],
        });
      }
      if (
        !Array.isArray(joint.range) ||
        joint.range.length !== 2 ||
        !isFiniteNumber(joint.range[0]) ||
        !isFiniteNumber(joint.range[1]) ||
        joint.range[1] <= joint.range[0]
      ) {
        findings.push({
          ruleId: "joint-limits",
          severity: "warning",
          category: "physics",
          title: "Joint has invalid range",
          message: `${jointLabel(joint)} has range [${joint.range?.join(", ")}].`,
          targets: [target("joint", joint.uuid, jointLabel(joint))],
        });
      }
      return findings;
    }),
};

const getActuatedJointUuids = (scene: Scene) => {
  const actuated = new Set<string>();
  Object.values(scene.actuators).forEach((actuator) => {
    if (actuator.joint) actuated.add(actuator.joint);
    const tendonUuid = actuator.tendonUuid;
    if (tendonUuid && scene.tendons[tendonUuid]) {
      scene.tendons[tendonUuid].joints.forEach((jointRef) => actuated.add(jointRef.joint));
    }
  });
  return actuated;
};

const validateUnactuatedJoints: ModelValidationRule = {
  id: "unactuated-joints",
  run: ({ scene }) => {
    const actuated = getActuatedJointUuids(scene);
    return Object.values(scene.joints)
      .filter((joint) => joint.kind !== "free" && !actuated.has(joint.uuid))
      .map((joint) => ({
        ruleId: "unactuated-joints",
        severity: "info",
        category: "physics",
        title: "Joint has no actuator",
        message: `${jointLabel(joint)} is not driven by a direct actuator or actuator-backed tendon.`,
        targets: [target("joint", joint.uuid, jointLabel(joint))],
      }));
  },
};

export const modelValidationRules: ModelValidationRule[] = [
  validateDanglingReferences,
  validateDegenerateInertials,
  validateMasslessMovingBodies,
  validateEmptyMovingBodies,
  validateJointLimits,
  validateUnactuatedJoints,
];

const makeFindingId = (finding: FindingInput, index: number) => {
  const targetPart = finding.targets.map((item) => `${item.kind}:${item.uuid}`).join("|");
  return `${finding.ruleId}:${targetPart || index}:${index}`;
};

export function validateModel(scene: Scene): ModelValidationResult {
  const context: RuleContext = { scene, ...buildSceneIndexes(scene) };
  const findings = modelValidationRules
    .flatMap((rule) => rule.run(context))
    .map((finding, index) => ({ ...finding, id: makeFindingId(finding, index) }));

  return {
    findings,
    summary: {
      errorCount: findings.filter((finding) => finding.severity === "error").length,
      warningCount: findings.filter((finding) => finding.severity === "warning").length,
      infoCount: findings.filter((finding) => finding.severity === "info").length,
      findingCount: findings.length,
    },
    checkedAt: Date.now(),
  };
}

export function getPrimaryFindingTarget(finding: ModelValidationFinding) {
  return finding.targets[0] || null;
}

export function isBodyUuid(uuid: string, scene: Scene): uuid is BodyUUID {
  return Object.prototype.hasOwnProperty.call(scene.bodies, uuid);
}
